/**
 * Validador de Reglas de Negocio
 * Realiza verificaciones de mayoría de edad, lista negra, aforo y cliente frecuente.
 * Cumple con .idx-rules.md: modular, funciones < 40 líneas.
 */

import { getConfig, getCapacity, incrementCapacity } from './config.js';
import { checkBlacklist, countVisitsLastMonth, saveScanLog } from '../infra/db.js';
import { hashDNI } from '../infra/crypto.js';
import { playSuccessChime, playVipChime, playErrorBuzzer } from '../infra/sound.js';
import { getCurrentUser } from '../ui/router.js';

/**
 * Calcula la edad en base a la fecha de nacimiento (YYYY-MM-DD).
 */
export function calculateAge(birthDateString) {
  if (!birthDateString) return 0;
  const birthDate = new Date(birthDateString);
  const today = new Date();
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/**
 * Valida un documento DNI escaneado aplicando todas las reglas de negocio.
 * Razón: Orquesta las validaciones de acceso y registra el log del evento.
 */
export async function validateScan(parsedData, scannerType, portero) {
  if (!parsedData || !parsedData.dni) {
    return { allowed: false, error: 'Documento no pudo ser leído o falta número de DNI.' };
  }

  const config = getConfig();
  const hashedDni = await hashDNI(parsedData.dni, config.secretWord);
  const user = getCurrentUser();
  const companyId = user ? user.company_id : null;

  // 1. Verificación de Lista Negra
  const blacklistInfo = await checkBlacklist(hashedDni, companyId);
  if (blacklistInfo) {
    return handleRejection(parsedData, hashedDni, 'BLACKLIST', blacklistInfo.reason, scannerType, portero, companyId);
  }

  // 2. Verificación de Edad
  const age = calculateAge(parsedData.birthDate);
  if (age < 18) {
    return handleRejection(parsedData, hashedDni, 'UNDERAGE', `Menor de edad (${age} años)`, scannerType, portero, companyId);
  }

  // 3. Verificación de Límite de Asistentes
  const capacityState = incrementCapacity();
  if (!capacityState.success) {
    return handleRejection(parsedData, hashedDni, 'CAPACITY_FULL', 'Límite de asistentes alcanzado', scannerType, portero);
  }

  // 4. Verificación de Cliente Frecuente (VIP)
  const visits = await countVisitsLastMonth(hashedDni, companyId);
  const isFrequent = (visits + 1) >= config.frequentThreshold; // +1 contando la entrada actual

  // Registro exitoso
  await saveScanLog({
    hashedDni,
    gender: parsedData.gender,
    birthDate: parsedData.birthDate,
    age,
    status: 'ALLOWED',
    portero,
    scannerType,
    company_id: companyId
  });

  triggerSuccessFeedback(isFrequent);

  return {
    allowed: true,
    dniAnonymized: hashedDni,
    fullName: parsedData.fullName,
    age,
    gender: parsedData.gender,
    isFrequent,
    visitCount: visits + 1,
    capacity: capacityState.current
  };
}

// Funciones auxiliares para respetar la regla de <40 líneas por función
async function handleRejection(parsed, hash, reason, detail, scannerType, portero, companyId) {
  playErrorBuzzer();
  await saveScanLog({
    hashedDni: hash,
    gender: parsed.gender || 'U',
    birthDate: parsed.birthDate || null,
    age: calculateAge(parsed.birthDate),
    status: 'REJECTED',
    rejectionReason: reason,
    portero,
    scannerType,
    company_id: companyId
  });
  return {
    allowed: false,
    reason,
    detail,
    fullName: parsed.fullName || 'Persona Rechazada',
    age: calculateAge(parsed.birthDate)
  };
}

function triggerSuccessFeedback(isFrequent) {
  if (isFrequent) {
    playVipChime();
  } else {
    playSuccessChime();
  }
}
